import { useMemo } from 'react';

import type { PatientRerequisitionEligibility } from '@customTypes/patient';
import {
  getReanalysisOrderingOptions,
  hasReanalysisOrderingOptions,
} from '@helpers/rerequisitionEligibility';
import { useActiveMethylationReflexDraftForRequisition, useActiveReanalysisDraftForRequisition } from '@hooks';
import { useSettings } from '@hooks/useSettings';

import PatientOrderMethylationReanalysisBanner from '../PatientDetailOrders/PatientDetailOrder/PatientOrderMethylationReanalysisBanner';
import PatientOrderReanalysisBanner from '../PatientDetailOrders/PatientDetailOrder/PatientOrderReanalysisBanner';

interface PatientDetailEligibilityBannersProps {
  eligibility: PatientRerequisitionEligibility | null;
}

/** Reanalysis and methylation reflex are one offer per patient, so they sit with demographics. */
export function PatientDetailEligibilityBanners({ eligibility }: PatientDetailEligibilityBannersProps) {
  const { isNateraProd } = useSettings();
  const originalRequisitionId = eligibility?.original_requisition_id;
  const reanalysisOrderingOptions = useMemo(() => getReanalysisOrderingOptions(eligibility), [eligibility]);
  const methylationProductIds = useMemo(
    () => (eligibility?.methylation_reflex_products ?? []).map(product => product.product_id),
    [eligibility?.methylation_reflex_products],
  );
  const showReanalysis = !isNateraProd && hasReanalysisOrderingOptions(eligibility) && Boolean(originalRequisitionId);
  const showMethylation = !isNateraProd && methylationProductIds.length > 0 && Boolean(originalRequisitionId);
  const { draftId: activeReanalysisDraftId } = useActiveReanalysisDraftForRequisition(
    originalRequisitionId,
    showReanalysis,
  );
  const { draftId: activeMethylationDraftId } = useActiveMethylationReflexDraftForRequisition(
    originalRequisitionId,
    showMethylation,
  );

  if (!showReanalysis && !showMethylation) {
    return null;
  }

  return (
    <div className='patient-detail-eligibility-banners'>
      {showReanalysis && originalRequisitionId ? (
        <PatientOrderReanalysisBanner
          requisitionId={originalRequisitionId}
          reanalysisProductIds={reanalysisOrderingOptions.productIds}
          reanalysisCampaignCode={reanalysisOrderingOptions.campaignCode}
          activeReanalysisDraftId={activeReanalysisDraftId}
        />
      ) : null}
      {showMethylation && originalRequisitionId ? (
        <PatientOrderMethylationReanalysisBanner
          requisitionId={originalRequisitionId}
          methylationProductIds={methylationProductIds}
          activeMethylationDraftId={activeMethylationDraftId}
        />
      ) : null}
    </div>
  );
}
